import React, { useContext, useState } from 'react';
import { AuthenticationContext } from '../../components/AuthenticationContext';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { GET_RECORD_INFO } from '../../graphql/queries/cameras';
import { FormProvider, useForm } from 'react-hook-form';
import UTextField2 from '../../utils/UTextField2';
import UCheckbox from '../../utils/UCheckbox';
import UDateTimePicker from '../../utils/UDateTimePicker';
import USelect from '../../utils/USelect';
import { GQL_GET_PLAYER } from '../../graphql/queries/players';
import { UPDATE_PLAYER_HOTLINK } from '../../graphql/mutations/players';

export default function HotLinkUpdate({
  camera,
  player,
  onClose,
  timezone,
}: {
  camera: string;
  player: any;
  onClose: any;
  timezone: string;
}) {
  const { removeToken } = useContext(AuthenticationContext);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<any>();
  const [start, setStart] = useState<any>(null);
  const [end, setEnd] = useState<any>(null);
  const [speeds, setSpeeds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [updatePlayerHotlink] = useMutation(UPDATE_PLAYER_HOTLINK, {
    refetchQueries: [{ query: GQL_GET_PLAYER, variables: { playerId: player.id } }],
    awaitRefetchQueries: true,
  });

  const hotlink = player?.settings?.video?.overlays?.hotlink?.label
    ? player?.settings?.video?.overlays?.hotlink
    : player?.settings?.image?.overlays?.hotlink;

  const methods = useForm({
    defaultValues: {
      enabled: hotlink.enabled,
      label: hotlink.label,
      time: hotlink.time,
      speed: hotlink.speed,
    },
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    criteriaMode: 'all',
    shouldFocusError: true,
  });
  const { handleSubmit, getValues, setValue } = methods;

  const findSpeeds = (time: number, info: any) => {
    return info.speeds
      .filter((speed: any) => speed.start < time && speed.end > time)
      .map((speed: any) => speed.speed)
      .sort((a: number, b: number) => a - b);
  };

  useQuery(GET_RECORD_INFO, {
    onError(error) {
      if (error.graphQLErrors.findIndex((err) => err?.extensions?.code == 'UNAUTHENTICATED') != -1) {
        removeToken();
      } else {
        console.error(error);
      }
      setLoading(false);
    },
    onCompleted(data) {
      setInfo(data.recordInfo);
      setStart(
        data.recordInfo.speeds.reduce((previous: any, speedInfo: any) => {
          if (previous == null) return speedInfo.start;
          if (speedInfo.start < previous) return speedInfo.start;
          return previous;
        }, null),
      );
      setEnd(
        data.recordInfo.speeds.reduce((previous: any, speedInfo: any) => {
          console.log(previous, speedInfo);
          if (previous == null) return speedInfo.start;
          if (speedInfo.end > previous) return speedInfo.end;
          return previous;
        }, null),
      );
      setSpeeds(findSpeeds(hotlink.time, data.recordInfo));
      setLoading(false);
    },
    variables: {
      cameraId: camera,
    },
    skip: !loading || !!info || !camera,
  });

  const onChange = (value: any) => {
    const speeds = findSpeeds(value, info);
    console.log('speeds', speeds);
    setSpeeds(speeds);
    const currentSpeed = getValues('speed');
    console.log('currentSpeed', currentSpeed);
    const shouldChangeSpeed = speeds.indexOf(currentSpeed) == -1;
    console.log('shouldChangeSpeed', shouldChangeSpeed);
    if (shouldChangeSpeed) setValue('speed', speeds[speeds.length - 1]);
    console.log('onCahnge', value);
  };

  const onSubmit = (form: any) => {
    setSaving(true);
    const variables = {
      input: {
        id: player.id,
        ...form,
      },
    };

    updatePlayerHotlink({ variables })
      .then(() => {
        setSaving(false);
        onClose();
      })
      .catch((err) => {
        setSaving(false);
        setError(`Failed to update account ${err.message}`);
        console.error(err);
      });
  };

  console.log(start, end, speeds, timezone);

  return (
    <>
      <Dialog
        open={!!player}
        onClose={onClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle id="alert-dialog-title">Hotlink</DialogTitle>
            <DialogContent>
              {error && <Alert severity="error">{error}</Alert>}
              <DialogContentText id="alert-dialog-description">{loading && 'Loading'}</DialogContentText>
              {info && !loading && (
                <Grid container spacing={2} marginTop="0">
                  <Grid item xs={12} container spacing={2}>
                    <Grid item xs>
                      <UTextField2
                        name="label"
                        options={{
                          required: 'Label is required',
                          minLength: { value: 3, message: 'Name must have at least 3 characters' },
                          maxLength: { value: 50, message: 'Name must have at max 50 characters' },
                        }}
                        fullWidth
                        label="Label"
                        shrink={true}
                        defaultValue=""
                        required
                      />
                    </Grid>
                    <Grid item xs="auto">
                      <UCheckbox name="enabled" label="Enabled" color="secondary" defaultValue={true} />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} container spacing={2}>
                    <Grid item xs>
                      <UDateTimePicker
                        name="time"
                        fullWidth
                        label="Time"
                        shrink={true}
                        timezone={timezone}
                        minDateTime={start}
                        maxDateTime={end}
                        required
                        onChange={onChange}
                      />
                    </Grid>
                    <Grid item xs="auto">
                      <USelect name="speed" fullWidth label="Speed" required>
                        {speeds.map((speed: number) => {
                          return (
                            <MenuItem value={speed} key={speed}>
                              {speed} x
                            </MenuItem>
                          );
                        })}
                      </USelect>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} color="secondary">
                Close
              </Button>
              <Button type="submit" color="secondary" disabled={saving}>
                Update
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </>
  );
}
